import { Button, Chip, Grid, TextField } from "@mui/material";
import Modal from "../../components/Modal/Modal";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { useState, useEffect } from "react";
import InputLabel from "../../components/Inputs/InputLabel";
import { AlertConfirm, AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { converterMoeda, displayMoney } from "../../utilities/displayValues";
import { formatDate } from "../../utilities/date";
import { ContasPagarType } from "../../types/ContasPagarType";
import { isPermite } from "../../utilities/isPermite";

type ModalContaPagarProps = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    contaPagar: ContasPagarType | null;
    setContaPagar: React.Dispatch<React.SetStateAction<ContasPagarType | null>>;
    reload: () => void;
};

type CategoriaPagar = { id_categoria_pagar: number; nome_categoria: string; };

export default function ModalContaPagar({ open, setOpen, contaPagar, setContaPagar, reload }: ModalContaPagarProps) {
    const [openLoad, setOpenLoad] = useState(false);
    const [descricao, setDescricao] = useState('');
    const [categoria, setCategoria] = useState('');
    const [data_vencimento, setDataVencimento] = useState('');
    const [valor_origem, setValorOrigem] = useState('');
    const [observacao, setObservacao] = useState('');
    const [categorias, setCategorias] = useState<CategoriaPagar[]>([]);

    function resetForm() {
        setDescricao('');
        setCategoria('');
        setDataVencimento('');
        setValorOrigem('');
        setObservacao('');
        setContaPagar(null);
    }

    function getSituacaoChip(cod: number) {
        if (cod === 1) return <Chip label="PAGO" color="success" sx={{ width: '100%' }} />;
        if (cod === 2) return <Chip label="EM ATRASO" color="error" sx={{ width: '100%' }} />;
        if (cod === 3) return <Chip label="CANCELADO" sx={{ width: '100%' }} />;
        return <Chip label="A VENCER" color="primary" sx={{ width: '100%' }} />;
    }

    async function handleSave() {
        if (openLoad) return;
        if (!descricao.trim()) return AlertInfo({ icon: 'error', title: 'Informe a descricao' });
        if (!data_vencimento) return AlertInfo({ icon: 'error', title: 'Informe a data de vencimento' });
        const valorNum = converterMoeda(valor_origem);
        if (isNaN(valorNum) || valorNum <= 0) return AlertInfo({ icon: 'error', title: 'Informe um valor valido' });
        setOpenLoad(true);
        try {
            const data: any = {
                descricao: descricao.trim(),
                categoria: categoria || null,
                data_vencimento,
                valor_origem: valorNum,
                observacao: observacao.trim() || null,
            };
            const response = await api.post('/createcontapagar', data);
            if (response.data.success) {
                AlertInfo({ icon: 'success', title: response.data.msg }).then(() => {
                    setOpen(false);
                    resetForm();
                    reload();
                });
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao cadastrar' });
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexao' });
        }
        setOpenLoad(false);
    }

    async function handleUpdate() {
        if (openLoad || !contaPagar) return;
        const valorNum = converterMoeda(valor_origem);
        if (isNaN(valorNum) || valorNum <= 0) return AlertInfo({ icon: 'error', title: 'Informe um valor valido' });
        setOpenLoad(true);
        try {
            const data: any = {
                descricao: descricao.trim(),
                categoria: categoria || null,
                data_vencimento,
                valor_origem: valorNum,
                observacao: observacao.trim() || null,
            };
            const response = await api.post(`/updatecontapagar/${contaPagar.id_pagar}`, data);
            if (response.data.success) {
                AlertInfo({ icon: 'success', title: response.data.msg }).then(() => {
                    setOpen(false);
                    resetForm();
                    reload();
                });
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao atualizar' });
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexao' });
        }
        setOpenLoad(false);
    }

    async function handlePagar() {
        if (openLoad || !contaPagar) return;
        const valorNum = Number(contaPagar.valor_origem);
        const dataPagamento = formatDate(new Date(), "usd");
        AlertConfirm({
            title: 'CONFIRMAR PAGAMENTO?',
            text: `Valor: ${displayMoney(valorNum)} | Data: ${formatDate(new Date())}`,
        }).then(async (res) => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const response = await api.post(`/pagarcontapagar/${contaPagar.id_pagar}`, {
                        valor_pago: valorNum,
                        data_pagamento: dataPagamento,
                    });
                    if (response.data.success) {
                        AlertInfo({ icon: 'success', title: 'Pagamento registrado!' }).then(() => {
                            setOpen(false);
                            resetForm();
                            reload();
                        });
                    } else {
                        AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao registrar' });
                    }
                } catch {
                    AlertInfo({ icon: 'error', title: 'Falha na conexao' });
                }
                setOpenLoad(false);
            }
        });
    }

    async function handleEstornar() {
        if (!contaPagar) return;
        AlertConfirm({ title: 'ESTORNAR PAGAMENTO?', text: 'O pagamento sera estornado e a conta voltara para "A Vencer".' }).then(async (res) => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const response = await api.post(`/estornarcontapagar/${contaPagar.id_pagar}`, {});
                    if (response.data.success) {
                        AlertInfo({ icon: 'success', title: 'Pagamento estornado!' }).then(() => {
                            setOpen(false);
                            resetForm();
                            reload();
                        });
                    } else {
                        AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao estornar' });
                    }
                } catch {
                    AlertInfo({ icon: 'error', title: 'Falha na conexao' });
                }
                setOpenLoad(false);
            }
        });
    }

    async function handleDeletar() {
        if (!contaPagar) return;
        AlertConfirm({ title: 'EXCLUIR CONTA?', text: 'Esta acao nao pode ser desfeita.' }).then(async (res) => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const response = await api.post(`/deletarcontapagar/${contaPagar.id_pagar}`, {});
                    if (response.data.success) {
                        AlertInfo({ icon: 'success', title: 'Conta excluida!' }).then(() => {
                            setOpen(false);
                            resetForm();
                            reload();
                        });
                    } else {
                        AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao excluir' });
                    }
                } catch {
                    AlertInfo({ icon: 'error', title: 'Falha na conexao' });
                }
                setOpenLoad(false);
            }
        });
    }

    useEffect(() => {
        if (!open) return;
        api.get('/categoriaspagar')
            .then(res => { if (Array.isArray(res.data)) setCategorias(res.data); })
            .catch(() => {});
    }, [open]);

    useEffect(() => {
        if (contaPagar && contaPagar.id_pagar) {
            setDescricao(contaPagar.descricao || '');
            setCategoria(contaPagar.categoria || '');
            setDataVencimento(formatDate(contaPagar.data_vencimento, "usd"));
            setValorOrigem(displayMoney(contaPagar.valor_origem));
            setObservacao(contaPagar.observacao || '');
        }
    }, [contaPagar]);

    const isEdicao = !!(contaPagar && contaPagar.id_pagar);
    const isPaga = isEdicao && contaPagar!.situacao_pagar === 1;
    const isCancelada = isEdicao && contaPagar!.situacao_pagar === 3;
    const podeEditar = isEdicao && !isPaga && !isCancelada;

    return (
        <>
            {/* Modal principal */}
            <Modal
                open={open}
                setOpen={setOpen}
                title={isEdicao ? "Conta a Pagar" : "Nova Conta a Pagar"}
                maxWidth="md"
                zIndex={2222}
                actionClose={resetForm}
            >
                <Grid container spacing={1}>
                    <SimpleBackdrop open={openLoad} />

                    {/* Status e acoes de edicao */}
                    {isEdicao && (
                        <>
                            <Grid item xs={12} sm={3}>
                                {getSituacaoChip(contaPagar!.situacao_pagar)}
                            </Grid>
                            <Grid item xs={12} sm={3} />
                            {podeEditar && isPermite('65') && (
                                <Grid item xs={12} sm={3}>
                                    <Button variant="contained" fullWidth color="success"
                                        onClick={handlePagar}>
                                        PAGAR
                                    </Button>
                                </Grid>
                            )}
                            {isPaga && isPermite('66') && (
                                <Grid item xs={12} sm={3}>
                                    <Button variant="outlined" fullWidth color="inherit" onClick={handleEstornar}>
                                        ESTORNAR
                                    </Button>
                                </Grid>
                            )}
                            {!isCancelada && !isPaga && isPermite('64') && (
                                <Grid item xs={12} sm={3}>
                                    <Button variant="contained" fullWidth color="error" onClick={handleDeletar}>
                                        EXCLUIR
                                    </Button>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* Campos do formulario */}
                    <Grid item xs={12} sm={8}>
                        <InputLabel
                            label="Descricao"
                            value={descricao}
                            setState={setDescricao}
                            isUpper
                            disabled={isPaga || isCancelada}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '100%' }}>
                            <span style={{ fontSize: 15 }}>Categoria</span>
                            <TextField
                                select
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={categoria}
                                onChange={e => setCategoria(e.target.value)}
                                disabled={isPaga || isCancelada}
                                SelectProps={{ native: true }}
                            >
                                <option value="">-- Selecione --</option>
                                {categorias.map(c => (
                                    <option key={c.id_categoria_pagar} value={c.nome_categoria}>
                                        {c.nome_categoria}
                                    </option>
                                ))}
                            </TextField>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel
                            label="Data de Vencimento"
                            type="date"
                            value={data_vencimento}
                            setState={setDataVencimento}
                            disabled={isPaga || isCancelada}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel
                            label="Valor"
                            value={valor_origem}
                            setState={setValorOrigem}
                            isMoney
                            disabled={isPaga || isCancelada}
                        />
                    </Grid>
                    {isPaga && (
                        <Grid item xs={12} sm={4}>
                            <InputLabel
                                label="Valor Pago"
                                value={displayMoney(contaPagar!.valor_pago || 0)}
                                disabled
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <InputLabel
                            label="Observacao"
                            value={observacao}
                            setState={setObservacao}
                            disabled={isPaga || isCancelada}
                        />
                    </Grid>

                    {/* Botoes de acao */}
                    {!isEdicao && (
                        <Grid item xs={12} sm={4} sx={{ mx: 'auto' }}>
                            <Button variant="contained" fullWidth color="primary" onClick={handleSave}>
                                CADASTRAR
                            </Button>
                        </Grid>
                    )}
                    {isEdicao && podeEditar && isPermite('63') && (
                        <Grid item xs={12} sm={4} sx={{ mx: 'auto' }}>
                            <Button variant="contained" fullWidth color="primary" onClick={handleUpdate}>
                                SALVAR ALTERACOES
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Modal>
        </>
    );
}
